import { trackEvent } from '@helpers/analytics';
import { Button, DeferredImage, useBreakpoint } from '@vivino/js-react-common-ui';
import { useI18N } from '@vivino/js-web-common';
import React, { useEffect, useState } from 'react';
import { fetchRegionSponsorship } from 'vivino-js/api/regionSponsorships';
import Card from 'vivino-ui/atoms/Card';

import styles from './meetTheRegion.module.scss';

interface MeetTheRegionProps {
  regionId: number;
  vintageId?: number;
}

interface ImageVariations {
  small: string;
  medium: string;
  large: string;
}

interface BasicImage {
  location: string;
  variations: ImageVariations;
}

interface RegionSponsorshipData {
  id: number;
  language: string;
  extract: string;
  content: string;
  content_html: string;
  partner_name: string;
  image: BasicImage;
  region_id: number;
}

interface RegionSponsorshipDataResponse {
  sponsorship: RegionSponsorshipData;
}

const TRANSLATIONS_PATH = 'components.shared.region_sponsorship';
const TRANSLATIONS = {
  meetTheRegion: `${TRANSLATIONS_PATH}.meet_the_region`,
  inPartnership: `${TRANSLATIONS_PATH}.in_partnership`,
  readMore: `${TRANSLATIONS_PATH}.read_more`,
};

const MeetTheRegion = ({ regionId, vintageId }: MeetTheRegionProps) => {
  const [sponsorship, setSponsorship] = useState<RegionSponsorshipData>(null);
  const { isMobile } = useBreakpoint();
  const { t } = useI18N();

  const sponsoredRegionId = sponsorship?.region_id || regionId;

  useEffect(() => {
    fetchRegionSponsorship({ regionId })
      .then((data: RegionSponsorshipDataResponse) => {
        setSponsorship(data.sponsorship);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (sponsorship) {
      trackEvent({
        event: 'Wine - Meet region - Show',
        props: {
          region_id: regionId,
          sponsored_region_id: sponsoredRegionId,
          vintage_id: vintageId,
        },
      });
    }
  }, [sponsorship]);

  const onButtonClick = () => {
    trackEvent({
      event: 'Wine - Meet region - Click',
      props: {
        region_id: regionId,
        sponsored_region_id: sponsoredRegionId,
        vintage_id: vintageId,
      },
    });
  };

  if (!sponsorship) {
    return null;
  }

  let imgSrc;
  if (sponsorship.image) {
    const { variations } = sponsorship.image;
    imgSrc = isMobile ? variations.small : variations.medium;
  }

  return (
    <>
      <Card className={styles.container}>
        <div className={styles.textWrapper}>
          <h3 className={styles.wineryName}>{t(TRANSLATIONS.meetTheRegion)}</h3>
          <div className={styles.sponsored}>
            {t(TRANSLATIONS.inPartnership, { partner: sponsorship.partner_name })}
          </div>
          <div className={styles.extract}>{sponsorship.extract}</div>
          <div className={styles.readMore}>
            <Button onClick={onButtonClick} href={`/wine-regions/${sponsoredRegionId}`}>
              {t(TRANSLATIONS.readMore)}
            </Button>
          </div>
        </div>
        <div className={styles.imageWrapper}>
          <DeferredImage src={imgSrc}>
            <div role="img" aria-label={'puglia'} className={styles.sponsoredImage} />
          </DeferredImage>
        </div>
      </Card>
    </>
  );
};

export default MeetTheRegion;
